import { useEffect, useState, useCallback, useMemo } from "react";
import api, { fmtEUR, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Users, Upload, Plus, FileText, ChevronDown, ChevronRight, Link2, Unlink, Pencil } from "lucide-react";
import { toast } from "sonner";

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const fmtMonth = (m) => {
  if (!m) return "—";
  const [y, mm] = m.split("-");
  return `${MESES[Number(mm) - 1] || mm} ${y}`;
};
const fmtH = (v) => (v === null || v === undefined ? "—" : `${v} h`);

function EmployeeForm({ open, onClose, onSaved, initial, branches }) {
  const [f, setF] = useState({});
  useEffect(() => {
    if (open) setF(initial ? { ...initial } : { name: "", puesto: "", branch_id: "", start_date: "", contract_hours_weekly: "", nif: "", email: "", phone: "" });
  }, [open, initial]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.name?.trim()) { toast.error("El nombre es obligatorio"); return; }
    const payload = {
      name: f.name.trim(), puesto: f.puesto || null, branch_id: f.branch_id || null,
      start_date: f.start_date || null, contract_hours_weekly: f.contract_hours_weekly ? Number(f.contract_hours_weekly) : null,
      nif: f.nif || null, email: f.email || null, phone: f.phone || null, active: initial ? initial.active !== false : true,
    };
    try {
      if (initial) await api.patch(`/payroll/employees/${initial.id}`, payload);
      else await api.post("/payroll/employees", payload);
      toast.success(initial ? "Empleado actualizado" : "Empleado creado");
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="employee-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{initial ? "Editar empleado" : "Nuevo empleado"}</DialogTitle>
          <DialogDescription>Datos personales y laborales. Los cambios de horas se gestionan con fecha de efecto en la ficha.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Nombre completo</Label>
            <Input data-testid="employee-form-name" value={f.name || ""} onChange={(e) => set("name", e.target.value)} /></div>
          <div><Label>Puesto</Label>
            <Input data-testid="employee-form-puesto" value={f.puesto || ""} onChange={(e) => set("puesto", e.target.value)} placeholder="Cocinero, Camarero..." /></div>
          <div><Label>Sucursal</Label>
            <Select value={f.branch_id || "none"} onValueChange={(v) => set("branch_id", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="employee-form-branch"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none" data-testid="employee-branch-none">Sin asignar</SelectItem>
                {branches.map((b) => <SelectItem key={b.id} value={b.id} data-testid={`employee-branch-${b.code || b.id}`}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div><Label>Inicio de contrato</Label>
            <Input data-testid="employee-form-start" type="date" value={f.start_date || ""} onChange={(e) => set("start_date", e.target.value)} /></div>
          {!initial && (
            <div><Label>Horas de contrato / semana</Label>
              <Input data-testid="employee-form-hours" type="number" min="0" step="0.5" value={f.contract_hours_weekly || ""} onChange={(e) => set("contract_hours_weekly", e.target.value)} /></div>
          )}
          <div><Label>NIF</Label>
            <Input data-testid="employee-form-nif" value={f.nif || ""} onChange={(e) => set("nif", e.target.value)} /></div>
          <div><Label>Email</Label>
            <Input data-testid="employee-form-email" type="email" value={f.email || ""} onChange={(e) => set("email", e.target.value)} /></div>
          <div><Label>Teléfono</Label>
            <Input data-testid="employee-form-phone" value={f.phone || ""} onChange={(e) => set("phone", e.target.value)} /></div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" data-testid="employee-form-cancel" onClick={onClose}>Cancelar</Button>
          <Button data-testid="employee-form-save" onClick={save}>Guardar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UploadDialog({ open, onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => { if (open) { setFile(null); setResult(null); } }, [open]);
  const upload = async () => {
    if (!file) { toast.error("Selecciona el PDF de la gestoría"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/payroll/payrolls/upload", fd);
      setResult(r.data);
      toast.success("Nómina procesada con OCR");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al procesar el PDF"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="upload-payroll-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Subir nómina (PDF gestoría)</DialogTitle>
          <DialogDescription>El OCR extrae empleado, período y desglose completo (bruto, IRPF, Seguridad Social, neto).</DialogDescription>
        </DialogHeader>
        <Input data-testid="upload-payroll-file" type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        {result && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm space-y-1" data-testid="upload-payroll-result">
            <div>Empleado: <b>{result.employee_matched ? result.employee_name : (result.employee_name_ocr || "No reconocido")}</b>
              {!result.employee_matched && result.employee_name_ocr && <Badge className="ml-2 bg-amber-100 text-amber-800">sin vincular</Badge>}</div>
            <div>Período: <b>{fmtMonth(result.month)}</b> · Neto: <b className="font-num">{fmtEUR(result.neto)}</b></div>
            <div className="text-xs text-slate-500">Revisa el desglose en la ficha del empleado.</div>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" data-testid="upload-payroll-cancel" onClick={onClose}>Cerrar</Button>
          <Button data-testid="upload-payroll-submit" onClick={upload} disabled={busy}>{busy ? "Procesando OCR..." : "Procesar"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PayrollRow({ p, canWrite, onChanged, showEmployee = false, employees = [] }) {
  const [open, setOpen] = useState(false);
  const [hours, setHours] = useState("");
  const [cands, setCands] = useState(null);
  const [linkEmp, setLinkEmp] = useState("");
  const linkEmployee = async () => {
    if (!linkEmp) { toast.error("Selecciona el empleado"); return; }
    try {
      await api.patch(`/payroll/payrolls/${p.id}`, { employee_id: linkEmp });
      toast.success("Nómina vinculada al empleado");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al vincular"); }
  };
  const viewPdf = async () => {
    try {
      const r = await api.get(`/payroll/payrolls/${p.id}/file`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
    } catch { toast.error("No se pudo abrir el PDF"); }
  };
  const saveHours = async () => {
    try {
      await api.patch(`/payroll/payrolls/${p.id}`, { hours_real: hours ? Number(hours) : null });
      toast.success("Horas reales registradas"); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar horas"); }
  };
  const loadCands = async () => {
    setCands("loading");
    try { setCands((await api.get(`/payroll/payrolls/${p.id}/candidates`)).data); }
    catch { setCands([]); toast.error("Error buscando candidatas"); }
  };
  const reconcile = async (txId) => {
    try {
      await api.post(`/payroll/payrolls/${p.id}/reconcile`, { tx_id: txId });
      toast.success("Nómina conciliada con la transferencia"); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al conciliar"); }
  };
  const unreconcile = async () => {
    try {
      await api.post(`/payroll/payrolls/${p.id}/unreconcile`);
      toast.success("Conciliación revertida"); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al desconciliar"); }
  };
  const pagada = p.payment_status === "pagada";
  return (
    <>
      <TableRow data-testid={`payroll-row-${p.id}`} className="cursor-pointer hover:bg-slate-50" onClick={() => setOpen(!open)}>
        <TableCell className="w-6">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</TableCell>
        <TableCell className="font-medium">{fmtMonth(p.month)}</TableCell>
        {showEmployee && (
          <TableCell className="text-sm">
            {p.employee_name || (
              <span className="text-amber-700">{p.employee_name_ocr || "—"} <Badge className="ml-1 bg-amber-100 text-amber-800">sin vincular</Badge></span>
            )}
          </TableCell>
        )}
        <TableCell className="text-right font-num">{fmtEUR(p.bruto)}</TableCell>
        <TableCell className="text-right font-num">{fmtEUR(p.neto)}</TableCell>
        <TableCell className="text-right font-num">{fmtH(p.horas_mes)}<span className="text-[10px] text-slate-400 ml-1">({p.horas_origen}{!p.hours_real && p.horas_semana ? ` · ${p.horas_semana} h/sem` : ""})</span></TableCell>
        <TableCell className="text-right font-num">{p.coste_hora !== null && p.coste_hora !== undefined ? `${fmtEUR(p.coste_hora)}/h` : "—"}</TableCell>
        <TableCell><Badge data-testid={`payroll-status-${p.id}`} className={pagada ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>{pagada ? "Pagada" : "Pendiente"}</Badge></TableCell>
      </TableRow>
      {open && (
        <TableRow data-testid={`payroll-detail-${p.id}`}>
          <TableCell colSpan={showEmployee ? 8 : 7} className="bg-slate-50/60 p-4">
            <div className="grid lg:grid-cols-2 gap-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Desglose del recibo</div>
                <Table>
                  <TableBody>
                    {(p.lines || []).map((l, i) => (
                      <TableRow key={`${l.concept}-${l.amount}-${i}`} data-testid={`payroll-line-${p.id}-${i}`}>
                        <TableCell className="py-1 text-sm">{l.concept}</TableCell>
                        <TableCell className="py-1 text-sm text-right font-num">
                          <span className={l.kind === "deduccion" ? "text-red-600" : ""}>{l.kind === "deduccion" ? "−" : ""}{fmtEUR(l.amount)}</span>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!(p.lines || []).length && <TableRow><TableCell className="py-1 text-sm text-slate-400">Sin líneas OCR</TableCell></TableRow>}
                  </TableBody>
                </Table>
                <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-sm max-w-sm">
                  <span className="text-slate-500">Bruto</span><span className="text-right font-num font-semibold" data-testid={`payroll-bruto-${p.id}`}>{fmtEUR(p.bruto)}</span>
                  <span className="text-slate-500">SS trabajador</span><span className="text-right font-num text-red-600" data-testid={`payroll-ss-trab-${p.id}`}>{p.ss_trabajador != null ? `−${fmtEUR(p.ss_trabajador)}` : "—"}</span>
                  <span className="text-slate-500">IRPF</span><span className="text-right font-num text-red-600" data-testid={`payroll-irpf-${p.id}`}>{p.irpf != null ? `−${fmtEUR(p.irpf)}` : "—"}</span>
                  <span className="text-slate-500">Neto</span><span className="text-right font-num font-bold" data-testid={`payroll-neto-${p.id}`}>{fmtEUR(p.neto)}</span>
                  <span className="text-slate-500">SS empresa</span><span className="text-right font-num" data-testid={`payroll-ss-emp-${p.id}`}>{fmtEUR(p.ss_empresa)}</span>
                  <span className="text-slate-500">Coste empresa</span><span className="text-right font-num font-semibold" data-testid={`payroll-coste-${p.id}`}>{p.bruto != null ? fmtEUR(p.bruto + (p.ss_empresa || 0)) : "—"}</span>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" data-testid={`payroll-pdf-${p.id}`} onClick={viewPdf}><FileText className="w-4 h-4 mr-1" />Ver PDF</Button>
                  {!pagada && canWrite && (
                    <Button size="sm" variant="outline" data-testid={`payroll-find-tx-${p.id}`} onClick={loadCands}><Link2 className="w-4 h-4 mr-1" />Buscar transferencia</Button>
                  )}
                  {pagada && canWrite && (
                    <Button size="sm" variant="outline" data-testid={`payroll-unreconcile-${p.id}`} onClick={unreconcile}><Unlink className="w-4 h-4 mr-1" />Desconciliar</Button>
                  )}
                </div>
                {!p.employee_id && canWrite && employees.length > 0 && (
                  <div className="flex items-end gap-2" data-testid={`payroll-link-${p.id}`}>
                    <div><Label className="text-xs">Vincular a empleado</Label>
                      <Select value={linkEmp} onValueChange={setLinkEmp}>
                        <SelectTrigger data-testid={`payroll-link-select-${p.id}`} className="w-56 h-9"><SelectValue placeholder="Selecciona empleado..." /></SelectTrigger>
                        <SelectContent>
                          {employees.map((e) => <SelectItem key={e.id} value={e.id} data-testid={`payroll-link-opt-${e.id}`}>{e.name}</SelectItem>)}
                        </SelectContent>
                      </Select></div>
                    <Button size="sm" data-testid={`payroll-link-save-${p.id}`} onClick={linkEmployee}>Vincular</Button>
                  </div>
                )}
                {canWrite && (
                  <div className="flex items-end gap-2">
                    <div><Label className="text-xs">Horas reales del mes (opcional)</Label>
                      <Input data-testid={`payroll-hours-real-${p.id}`} type="number" min="0" step="0.5" className="w-32 h-9"
                        placeholder={String(p.horas_mes || "")} value={hours} onChange={(e) => setHours(e.target.value)} /></div>
                    <Button size="sm" data-testid={`payroll-hours-save-${p.id}`} onClick={saveHours}>Guardar horas</Button>
                  </div>
                )}
                {cands === "loading" && <div className="text-sm text-slate-400">Buscando movimientos...</div>}
                {Array.isArray(cands) && (
                  <div data-testid={`payroll-candidates-${p.id}`}>
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                      Transferencias candidatas (empleado + importe)
                    </div>
                    {!cands.length && <div className="text-sm text-slate-400">Sin candidatas: no hay cargos pendientes que casen por nombre e importe.</div>}
                    {cands.map((c) => (
                      <div key={c.tx_id} data-testid={`payroll-cand-${c.tx_id}`}
                        className="flex items-center justify-between gap-2 border border-slate-200 rounded-lg px-3 py-2 mb-1.5 text-sm bg-white">
                        <div className="min-w-0">
                          <div className="truncate font-medium">{c.concept}</div>
                          <div className="text-xs text-slate-400">{fmtDate(c.date)} · <span className="font-num">{fmtEUR(Math.abs(c.amount))}</span>
                            {c.amount_diff > 0 && <span className="text-amber-600"> · dif {fmtEUR(c.amount_diff)}</span>}</div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge className={c.name_match ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}>
                            {c.name_match ? "Nombre ✓" : "Solo importe"}
                          </Badge>
                          {canWrite && <Button size="sm" data-testid={`payroll-reconcile-${c.tx_id}`} onClick={() => reconcile(c.tx_id)}>Vincular</Button>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function EmployeeDetail({ empId, open, onClose, branches, canWrite, onEdit }) {
  const [emp, setEmp] = useState(null);
  const [hf, setHf] = useState({ hours: "", effective_from: new Date().toISOString().slice(0, 10), note: "" });
  const load = useCallback(() => {
    if (empId && open) api.get(`/payroll/employees/${empId}`).then((r) => setEmp(r.data)).catch(() => toast.error("Error cargando empleado"));
  }, [empId, open]);
  useEffect(() => { load(); }, [load]);
  const sortedHours = useMemo(
    () => [...(emp?.hours_history || [])].sort((a, b) => (b.effective_from || "").localeCompare(a.effective_from || "")),
    [emp]);
  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";
  const saveHours = async () => {
    if (!hf.hours || Number(hf.hours) <= 0) { toast.error("Horas inválidas"); return; }
    try {
      await api.post(`/payroll/employees/${empId}/hours`, { hours: Number(hf.hours), effective_from: hf.effective_from, note: hf.note || null });
      toast.success("Cambio de horas registrado");
      setHf({ hours: "", effective_from: new Date().toISOString().slice(0, 10), note: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al registrar horas"); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="employee-detail-dialog">
        {!emp ? (
          <>
            <DialogHeader><DialogTitle className="sr-only">Ficha de empleado</DialogTitle></DialogHeader>
            <div className="py-8 text-center text-slate-400">Cargando...</div>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <DialogTitle className="font-heading text-xl" data-testid="employee-detail-name">{emp.name}</DialogTitle>
                {canWrite && <Button size="sm" variant="outline" data-testid="employee-edit-btn" onClick={() => onEdit(emp)}><Pencil className="w-4 h-4 mr-1" />Editar datos</Button>}
              </div>
              <DialogDescription>{emp.puesto || "Sin puesto"} · {branchName(emp.branch_id)} · Alta: {fmtDate(emp.start_date)}</DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                ["Horas contrato / semana", emp.hours_current != null ? `${emp.hours_current} h` : "—", "emp-hours-current"],
                ["Nóminas registradas", String((emp.payrolls || []).length), "emp-n-payrolls"],
                ["NIF", emp.nif || "—", "emp-nif"],
                ["Contacto", emp.email || emp.phone || "—", "emp-contact"],
              ].map(([l, v, t]) => (
                <div key={t} data-testid={t} className="rounded-lg border border-slate-200 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">{l}</div>
                  <div className="font-semibold text-sm mt-0.5 truncate">{v}</div>
                </div>
              ))}
            </div>

            <Card data-testid="emp-hours-history">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold font-heading">Histórico de horas de contrato (semanales)</CardTitle></CardHeader>
              <CardContent>
                <Table>
                  <TableHeader><TableRow><TableHead>Horas/semana</TableHead><TableHead>Desde</TableHead><TableHead>Nota</TableHead><TableHead>Registrado por</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {sortedHours.map((h, i) => (
                      <TableRow key={`${h.effective_from}-${h.hours}`} data-testid={`hours-history-row-${i}`}>
                        <TableCell className="font-num">{fmtH(h.hours)}</TableCell>
                        <TableCell>{fmtDate(h.effective_from)}</TableCell>
                        <TableCell className="text-sm text-slate-500">{h.note || "—"}</TableCell>
                        <TableCell className="text-xs text-slate-400">{h.changed_by}</TableCell>
                      </TableRow>
                    ))}
                    {!(emp.hours_history || []).length && <TableRow><TableCell colSpan={4} className="text-slate-400 text-sm">Sin cambios registrados</TableCell></TableRow>}
                  </TableBody>
                </Table>
                {canWrite && (
                  <div className="flex items-end gap-2 mt-3 flex-wrap">
                    <div><Label className="text-xs">Nuevas horas/semana</Label>
                      <Input data-testid="hours-change-hours" type="number" min="0" step="0.5" className="w-28 h-9" value={hf.hours} onChange={(e) => setHf({ ...hf, hours: e.target.value })} /></div>
                    <div><Label className="text-xs">Efectivo desde</Label>
                      <Input data-testid="hours-change-date" type="date" className="w-40 h-9" value={hf.effective_from} onChange={(e) => setHf({ ...hf, effective_from: e.target.value })} /></div>
                    <div className="flex-1 min-w-40"><Label className="text-xs">Nota</Label>
                      <Input data-testid="hours-change-note" className="h-9" placeholder="Reducción de jornada..." value={hf.note} onChange={(e) => setHf({ ...hf, note: e.target.value })} /></div>
                    <Button size="sm" data-testid="hours-change-save" onClick={saveHours}>Registrar cambio</Button>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card data-testid="emp-payrolls">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold font-heading">Nóminas</CardTitle></CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="w-6"></TableHead><TableHead>Mes</TableHead>
                    <TableHead className="text-right">Bruto</TableHead><TableHead className="text-right">Neto</TableHead>
                    <TableHead className="text-right">Horas</TableHead><TableHead className="text-right">Coste/hora</TableHead><TableHead>Estado</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {(emp.payrolls || []).map((p) => <PayrollRow key={p.id} p={p} canWrite={canWrite} onChanged={load} />)}
                    {!(emp.payrolls || []).length && <TableRow><TableCell colSpan={7} className="text-slate-400 text-sm py-4 text-center">Sin nóminas — sube el PDF de la gestoría</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Nominas() {
  const { org, user } = useAuth();
  const canWrite = ["ADMIN", "FINANCE"].includes(user?.role);
  const [employees, setEmployees] = useState(null);
  const [payrolls, setPayrolls] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editEmp, setEditEmp] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(() => {
    api.get("/payroll/employees").then((r) => setEmployees(r.data)).catch(() => toast.error("Error cargando empleados"));
    api.get("/payroll/payrolls").then((r) => setPayrolls(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const pendientes = (employees || []).reduce((s, e) => s + (e.pending_payrolls || 0), 0);

  return (
    <div className="space-y-6" data-testid="nominas-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Personal y Nóminas</h1>
          <p className="text-sm text-slate-500 mt-1">Empleados, histórico de horas, nóminas mensuales con desglose y conciliación con transferencias</p>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <Button variant="outline" data-testid="btn-upload-payroll" onClick={() => setShowUpload(true)}><Upload className="w-4 h-4 mr-1" />Subir nómina PDF</Button>
            <Button data-testid="btn-new-employee" onClick={() => { setEditEmp(null); setShowForm(true); }}><Plus className="w-4 h-4 mr-1" />Nuevo empleado</Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {[
          { label: "Empleados", value: employees ? String(employees.length) : "—", icon: Users, testid: "kpi-employees" },
          { label: "Nóminas pendientes de conciliar", value: employees ? String(pendientes) : "—", icon: Link2, testid: "kpi-pending-payrolls" },
          { label: "Último mes con nómina", value: employees ? fmtMonth(employees.map((e) => e.latest_month).filter(Boolean).sort().pop()) : "—", icon: FileText, testid: "kpi-latest-month" },
        ].map((c) => (
          <Card key={c.testid} data-testid={c.testid}>
            <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm font-medium text-slate-500 uppercase tracking-wider">{c.label}</CardTitle>
              <c.icon className="w-4 h-4 text-slate-400" />
            </CardHeader>
            <CardContent><div className="text-2xl font-extrabold font-num tracking-tight">{c.value}</div></CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base font-semibold font-heading">Empleados</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table data-testid="employees-table">
            <TableHeader><TableRow>
              <TableHead>Nombre</TableHead><TableHead>Puesto</TableHead><TableHead>Sucursal</TableHead>
              <TableHead className="text-right">Horas/sem</TableHead><TableHead className="text-right">Nóminas</TableHead>
              <TableHead>Último mes</TableHead><TableHead className="text-right">Último neto</TableHead>
              <TableHead className="text-right">Coste/hora</TableHead><TableHead className="text-right">Pendientes</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(employees || []).map((e) => (
                <TableRow key={e.id} data-testid={`employee-row-${e.id}`} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailId(e.id)}>
                  <TableCell className="font-medium">{e.name}</TableCell>
                  <TableCell className="text-sm text-slate-500">{e.puesto || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-500">{org.branches.find((b) => b.id === e.branch_id)?.name || "—"}</TableCell>
                  <TableCell className="text-right font-num">{fmtH(e.contract_hours_weekly)}</TableCell>
                  <TableCell className="text-right font-num">{e.n_payrolls}</TableCell>
                  <TableCell>{fmtMonth(e.latest_month)}</TableCell>
                  <TableCell className="text-right font-num">{fmtEUR(e.latest_neto)}</TableCell>
                  <TableCell className="text-right font-num">{e.latest_coste_hora != null ? `${fmtEUR(e.latest_coste_hora)}/h` : "—"}</TableCell>
                  <TableCell className="text-right font-num">{e.pending_payrolls > 0 ? <Badge className="bg-amber-100 text-amber-800">{e.pending_payrolls}</Badge> : "0"}</TableCell>
                </TableRow>
              ))}
              {employees && !employees.length && (
                <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-8">Sin empleados — crea el primero con «Nuevo empleado»</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card data-testid="payrolls-card">
        <CardHeader><CardTitle className="text-base font-semibold font-heading">Nóminas registradas</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table data-testid="payrolls-table">
            <TableHeader><TableRow>
              <TableHead className="w-6"></TableHead><TableHead>Mes</TableHead><TableHead>Empleado</TableHead>
              <TableHead className="text-right">Bruto</TableHead><TableHead className="text-right">Neto</TableHead>
              <TableHead className="text-right">Horas</TableHead><TableHead className="text-right">Coste/hora</TableHead><TableHead>Estado</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(payrolls || []).map((p) => (
                <PayrollRow key={p.id} p={p} canWrite={canWrite} onChanged={load} showEmployee employees={employees || []} />
              ))}
              {payrolls && !payrolls.length && (
                <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8">Sin nóminas — sube el PDF de la gestoría</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <EmployeeForm open={showForm} onClose={() => setShowForm(false)} onSaved={load} initial={editEmp} branches={org.branches} />
      <UploadDialog open={showUpload} onClose={() => setShowUpload(false)} onSaved={load} />
      <EmployeeDetail empId={detailId} open={!!detailId} onClose={() => setDetailId(null)} branches={org.branches} canWrite={canWrite}
        onEdit={(emp) => { setEditEmp(emp); setShowForm(true); }} />
    </div>
  );
}
